/**
 * Keeps the floating message button out of the shopper's way.
 *
 * Two things get in each other's way at the bottom right of the screen:
 *
 * - The product grid scrolls under the button, which is fixed, so about a
 *   quarter of all scroll positions leave it sitting on a card's order button.
 *   Moving the button does not change that, because the cards repeat down the
 *   page, so instead it stops taking taps while the page is moving.
 * - A sticky bar slides up into the same corner, on the product page to add to
 *   cart and on the landing page to confirm the order. The button rides above
 *   whichever one is showing.
 */

/** How long after the last scroll event the button starts taking taps again. */
const SETTLE_DELAY = 250;

const SCROLLING_CLASS = 'floating-message--scrolling';

/** Gap left between the sticky add-to-cart bar and the button above it. */
const BAR_GAP = 12;

/**
 * Bars that slide into the same corner and flip `data-stuck` when they do: the
 * product page's add to cart bar, and the landing page's confirm bar.
 */
const STUCK_BAR = '.sticky-add-to-cart__bar[data-stuck="true"], .landing-offer__sticky[data-stuck="true"]';

const button = document.querySelector('.floating-message');

if (button) {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let settleTimer;

  window.addEventListener(
    'scroll',
    () => {
      button.classList.add(SCROLLING_CLASS);

      // Re-armed on every event, so a swallowed or missed scroll can never leave
      // the button permanently untappable.
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => button.classList.remove(SCROLLING_CLASS), SETTLE_DELAY);
    },
    { passive: true }
  );

  /** The bar currently being watched for height changes, if any. */
  let observedBar = /** @type {Element | null} */ (null);

  const barResizeObserver = new ResizeObserver(() => syncBarClearance());

  /**
   * Tells the stylesheet how much room the sticky add-to-cart bar needs. The
   * button's own offset wins whenever this is the smaller of the two, so the
   * clearance goes back to zero the moment the bar hides.
   */
  function syncBarClearance() {
    const bar = document.querySelector(STUCK_BAR);

    // `offsetHeight` and the computed `bottom` both ignore the bar's slide-in
    // transform, so the clearance is right from the first frame rather than
    // creeping up over the bar's animation.
    // A bar that is sticky rather than fixed computes its bottom as `auto`,
    // which parses to NaN. That used to reach the stylesheet as `NaNpx`, and an
    // invalid length there throws away the whole inset, which drops the button
    // into the top left corner of the page. Anything that is not a number is
    // treated as a bar that needs no room.
    const barBottom = bar ? parseFloat(getComputedStyle(bar).bottom) : 0;
    const clearance =
      bar && Number.isFinite(barBottom)
        ? barBottom + /** @type {HTMLElement} */ (bar).offsetHeight + BAR_GAP
        : 0;

    button.style.setProperty('--floating-message-bar-clearance', `${Math.round(clearance)}px`);

    if (bar !== observedBar) {
      if (observedBar) barResizeObserver.unobserve(observedBar);
      if (bar) barResizeObserver.observe(bar);
      observedBar = bar;
    }
  }

  syncBarClearance();

  // The bar flips data-stuck as the shopper scrolls past the buy buttons, and is
  // re-rendered on every variant change, so the attribute is watched across the
  // document rather than on one element that may be replaced.
  new MutationObserver(syncBarClearance).observe(document.body, {
    subtree: true,
    attributes: true,
    attributeFilter: ['data-stuck'],
  });

  window.addEventListener('resize', syncBarClearance, { passive: true });
}
